import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import { AppConfig } from 'src/app/app.config';
import { ExternalAreaImportRequest, ExternalRequest, ExternalRequestTargetType, ExternalRequestType, ExternalUnitImportRequest, PermitRequest, SimplePriceList } from './external-request.model';

@Injectable()
export class ExternalRequestService {

    constructor(
        private http: HttpClient,
        private config: AppConfig) { }

    public getAvailablePriceLists(): Observable<SimplePriceList[]> {
        const url = this.config.backendAmgUri + 'Api/PriceLists/Default.GetAvailablePriceLists';

        return this.http.get<any>(url)
            .pipe(
                map(result => result.value as SimplePriceList[]),
                map(results => results.sort((a, b) => a.Name.localeCompare(b.Name)))
            );
    }

    public getAttachmentLinkValue(targetType: ExternalRequestTargetType, targetId: string, linkName: string): Observable<string | null> {
        const apiName = targetType === 'Task' ? 'Api' : `${targetType}Api`;
        const url = this.config.backendIwmUri +
            `${apiName}/${targetType}Links?$filter=TargetId eq ${targetId} and Name eq '${linkName}'&$select=Value`;

        return this.http.get<any>(url)
            .pipe(
                map(result => (result.value[0]?.Value ?? null) as string | null)
            );
    }

    public getExternalRequestHistory(
        targetType: ExternalRequestTargetType,
        targetId: string,
        requestType?: ExternalRequestType,
        companyId?: string,
        resultLimit: number = 5): Observable<ExternalRequest[]> {

        let filters: string[] = [];

        if (requestType) {
            filters.push(`RequestType eq '${requestType}'`);
        }
        if (companyId) {
            filters.push(`CompanyId eq ${companyId}`);
        }

        let params = [
            `$orderBy=RequestCreated desc`
        ];

        if (resultLimit) {
            params.push(`$top=${resultLimit}`);
        }
        if (filters.length > 0) {
            params.push(`$filter=${filters.join(' and ')}`);
        }

        const url = this.config.backendIwmUri +
            `CustomerAdminApi/ExternalRequests/GetForTarget(targetType='${targetType}', targetId=${targetId})?${params.join('&')}`;

        return this.http.get<any>(url)
            .pipe(
                map(result => result.value as ExternalRequest[])
            );
    }

    public getExternalAreaImportAccess(targetType: ExternalRequestTargetType, targetId: string): Observable<boolean> {
        const url = this.config.backendIwmUri +
            `CustomerAdminApi/ExternalRequests/HasExternalAreaImportAccessForTarget(targetType='${targetType}', targetId=${targetId})`;

        return this.http.get<any>(url)
            .pipe(
                map(result => result.value as boolean)
            );
    }

    public requestExternalUnitImport(config: ExternalUnitImportRequest): Observable<ExternalRequest> {
        const url = this.config.backendIwmUri + 'CustomerAdminApi/ExternalRequests/RequestExternalUnitImport';

        return this.http.post<ExternalRequest>(url, config);
    }

    public requestExternalAreaImport(config: ExternalAreaImportRequest): Observable<ExternalRequest> {
        const url = this.config.backendIwmUri + 'CustomerAdminApi/ExternalRequests/RequestExternalAreaImport';

        return this.http.post<ExternalRequest>(url, config);
    }

    public sendPermitRequest(config: PermitRequest): Observable<ExternalRequest> {
        const url = `${this.config.backendIwmUri}CustomerAdminApi/ExternalRequests/Default.SendPermitRequest`;
        return this.http.post<ExternalRequest>(url, config);
    }

    public getAllowedRequestTypes(taskId: string): Observable<Array<string>> {
        const url = `${this.config.backendIwmUri}CustomerAdminApi/ExternalRequests/Default.GetAllowedPermitRequestTypes(taskId=${taskId})`;
        return this.http.get<any>(url)
            .pipe(
                map(result => result.value)
            );
    }
}
